import { ChangeEvent, HTMLAttributes, useEffect, useRef, useState } from 'react'
import { map } from 'lodash'
import Link from 'next/link'
import { useRouter } from 'next/router'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Logo from '@/components/base/logo'
import Section from '@/components/base/section'
import Spinner from '@/components/base/spinner'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { useAppStore } from '@/contexts/store'
import { API, MOCK_DATA } from '@/constants'
import { axiosServer as axios } from '@/base/axios'

import BorderedCard from '../base/bordered-card'
import { cn } from '@/lib/utils'
import { SearchResults } from '@/types'

export type SearchHeaderProps = HTMLAttributes<HTMLDivElement>

const SearchHeader = ({}: SearchHeaderProps) => {
  const router = useRouter()
  const { strings } = useAppStore()

  return (
    <div className="fixed left-0 right-0 top-0 z-header p-[1rem] md:p-[2rem]">
      <div className="relative mx-auto grid w-full max-w-[71.1rem]">
        <div
          className={cn(
            'group/header',
            'relative size-full rounded-[0.7rem] px-[2rem]',
            'bg-dark text-white md:px-[3rem]'
          )}
        >
          <div className="flex h-header-height items-center justify-between">
            <Link
              href="/"
              scroll={false}
              className={cn('-m-3 rounded-[0.7rem] p-3', 'focus-ring-light')}
            >
              <span className="sr-only">{strings?.brand}</span>
              <Logo />
            </Link>

            <div className="shrink-0">
              <button
                onClick={() => router.back()}
                className={cn(
                  'relative -mx-[1.25rem] flex size-[4.4rem] items-center justify-center rounded-[0.5rem]',
                  'focus-ring-light'
                )}
              >
                <span className="sr-only">{strings?.labels?.close}</span>
                <SVGIcon
                  symbol="plus"
                  className={cn(
                    'size-[1.2rem] rotate-[135deg]',
                    'transition-transform duration-500 ease-out-cubic'
                  )}
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export type SearchProps = HTMLAttributes<HTMLDivElement>

const DEBOUNCE_INTERVAL = 500
const MINIMUM_DIGIT = 3

const Search = ({ className, ...props }: SearchProps) => {
  const { strings } = useAppStore()
  const [searchValue, setSearchValue] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null)
  const [isLoading, setLoading] = useState(false)
  const input = useRef<HTMLInputElement>(null!)
  const typingTimer = useRef<any>(null)

  const handleChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }

  useEffect(() => {
    if (searchValue.length < MINIMUM_DIGIT) return

    clearTimeout(typingTimer.current)
    typingTimer.current = setTimeout(async () => {
      setLoading(true)
      const { data: results } = MOCK_DATA
        ? { data: require('@/data/api/search.json') as SearchResults }
        : await axios.get<SearchResults>(API.SEARCH, {
            params: { s: searchValue, per_page: 10 }
          })
      setSearchResults(results)
      setLoading(false)
    }, DEBOUNCE_INTERVAL)

    return () => {
      clearTimeout(typingTimer.current)
    }
  }, [searchValue])

  useEffect(() => {
    input.current.focus()
  }, [])

  return (
    <Section className={cn('min-h-svh-vh bg-dark pb-[20vh] text-white', className)} {...props}>
      <SearchHeader />

      <Container>
        <Grid className="items-start gap-y-[5rem]">
          <div className="col-span-2 md:col-span-6 lg:col-span-12">
            <form
              className={cn(
                'mx-auto w-full max-w-[71.1rem] px-[1rem] md:px-[3rem]',
                'pb-[20vh] pt-[calc(var(--header-height)+20vh)]'
              )}
              onSubmit={(event) => event.preventDefault()}
            >
              <div className="relative size-full">
                <input
                  ref={input}
                  type="text"
                  spellCheck="false"
                  value={searchValue}
                  onChange={handleChangeInput}
                  placeholder={strings?.labels?.search}
                  className={cn(
                    'w-full appearance-none border-b pr-[4rem] outline-none',
                    'font-helvetica text-s-standard md:text-d-standard',
                    'border-[#BFBFBF]/40 bg-transparent text-[#BFBFBF] placeholder:text-[#BFBFBF]/40'
                  )}
                />

                {isLoading ? (
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 text-[#BFBFBF]">
                    <Spinner />
                  </div>
                ) : null}
              </div>
            </form>
          </div>

          {searchResults ? (
            searchResults.total === 0 && searchValue.length >= MINIMUM_DIGIT ? (
              <div className="col-span-2 md:col-span-6 lg:col-span-12">
                <Typography variant="history-size-body">{strings?.labels?.no_results}</Typography>
              </div>
            ) : (
              <>
                <div className="relative col-span-2 lg:col-span-3">
                  <span className="font-helvetica text-main-title-default font-bold">{`${searchResults.total}`}</span>
                  <span className="font-helvetica text-main-title-default">
                    {` – ${strings?.labels?.results}`}
                  </span>
                </div>

                {map(
                  [
                    ...searchResults?.results.taxonomies,
                    ...searchResults?.results.posts,
                    ...searchResults?.results.pages,
                    ...searchResults?.results.products,
                    ...searchResults?.results.colors
                  ],
                  (card) => (
                    <BorderedCard
                      key={`card-${card.id}`}
                      title={card.title}
                      link={card.link}
                      image={card.image}
                      variant="compact"
                      className="col-span-2 lg:col-span-3"
                      fill={
                        card.type === 'product' || (card.type === 'taxonomy' && card.parent !== 0)
                          ? false
                          : true
                      }
                    />
                  )
                )}
              </>
            )
          ) : null}
        </Grid>
      </Container>
    </Section>
  )
}

export default Search
